import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import React, { useEffect } from "react";
import { FormattedMessage } from "react-intl";
import FileUploader from "./FileUploader";
import { Field, Form } from "react-final-form";
import { SpecViewer } from "./SpecViewer";
import ApiService, { ApiStandardVersion } from "../services/ApiService";
import CustomFileInput from "./CustomFileInput";
import Alert from "@material-ui/lab/Alert/Alert";
import { resolveAPIErrorMessage } from "modus/components/error-message/ErrorMessage";
import { useAsyncAction } from "legacy-components/form/action/AsyncAction";

interface SpecCreatorProps {
  apiId: string;
  version: string;
  onOpen: boolean;
  onClose: () => void;
  refreshDeployments: () => void;
}

export const SpecCreator: React.FC<SpecCreatorProps> = ({
  apiId,
  version,
  onOpen,
  onClose,
  refreshDeployments,
}) => {
  let submitData = new FormData();

  const [uploadFile, setUploadedFile] = React.useState<string>();
  const [apiStandardVersion, setApiStandardVersion] = React.useState<string>("");
  const [apiStandardVersions, setApiStandardVersions] = React.useState<ApiStandardVersion[]>([]);
  const [apiStandardVersionError, setApiStandardVersionError] = React.useState<string>("");
  const formRef = React.useRef<any>(null);

  const [state, { subscribe }] = useAsyncAction(() =>
    ApiService.uploadSpec(apiId, version, submitData)
  );
  const [apiStandardVersionsState, { subscribe: fetchApiStandardVersions }] = useAsyncAction(() =>
    ApiService.getApiStandardVersions()
  );

  const clear = () => {
    setUploadedFile(undefined);
    setApiStandardVersion("");
    if (formRef.current) {
      formRef.current.change("specFile", undefined);
      formRef.current.change("apiStandardVersion", undefined);
    }
  };

  const onSubmit = async (values: any) => {
    if (!values.specFile) {
      return;
    }
    if (!values.apiStandardVersion) {
      setApiStandardVersionError("Unable to upload specification because API standard versions are unavailable. Please try again later.");
      return;
    }
    submitData = new FormData();
    submitData.append("file", values.specFile[0]);
    submitData.append("apiStandardVersion", values.apiStandardVersion);
    subscribe();
  };

  useEffect(() => {
    if (!onOpen) {
      return;
    }

    setApiStandardVersionError("");
    fetchApiStandardVersions();
  }, [onOpen, fetchApiStandardVersions]);

  useEffect(() => {
    if (apiStandardVersionsState.error) {
      setApiStandardVersions([]);
      setApiStandardVersion("");
      setApiStandardVersionError("Unable to load API standard versions. Please try again later.");
      if (formRef.current) {
        formRef.current.change("apiStandardVersion", undefined);
      }
      return;
    }

    if (!apiStandardVersionsState.loaded) {
      return;
    }

    const versions = (apiStandardVersionsState.value as ApiStandardVersion[]) || [];
    if (versions.length === 0) {
      setApiStandardVersions([]);
      setApiStandardVersion("");
      setApiStandardVersionError("No API standard versions are available right now. Please try again later.");
      if (formRef.current) {
        formRef.current.change("apiStandardVersion", undefined);
      }
      return;
    }

    const defaultVersion = versions[0].version;
    setApiStandardVersions(versions);
    setApiStandardVersion(defaultVersion);
    setApiStandardVersionError("");
    if (formRef.current) {
      formRef.current.change("apiStandardVersion", defaultVersion);
    }
  }, [apiStandardVersionsState]);

  useEffect(() => {
    if (!state.loaded || !onClose) {
      return;
    }
    if (state.loaded) {
      clear();
      onClose();
      refreshDeployments();
    }

 
  }, [state.loaded, onClose, refreshDeployments]);

  useEffect(() => {
    if(!refreshDeployments || !state.error) {
      return;
    }

    if (state.error  && !onOpen) {
      refreshDeployments();
    }
  }, [state.error, onOpen, refreshDeployments]);

  return (
    <Dialog
      open={onOpen}
      onClose={() => {
          onClose();  
          setTimeout(() => {
            clear();
          }, 100);
      }}
      title="Update the Specification File"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
         <Box display="flex" alignItems="center" justifyContent="space-between">
          <FormattedMessage defaultMessage="Update the Specification File" />
          <IconButton 
            aria-label="close"
            onClick={() => {
              onClose();
              setTimeout(() => {
            clear();
          }, 100);
            }}
            size="small"
            style={{ marginRight: -12, marginTop: -12 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent style={{ overflow: "hidden", paddingBottom: 0 }}>
        {apiStandardVersionError && <Alert severity="error">{apiStandardVersionError}</Alert>}
        {!uploadFile && (
          <Box>
            <Box
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <FormattedMessage defaultMessage="Upload your modified Specification file" />
            </Box>
            <Box style={{ marginBottom: "16px" }}>
              <Form
                onSubmit={onSubmit}
                render={({ handleSubmit, submitting, pristine, form }) => {
                  formRef.current = form;
                  return (
                    <form id="spec-form" onSubmit={handleSubmit}>
                      <Box
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <Field
                          name="specFile"
                          component={CustomFileInput}
                          accept={[".json", ".yaml", ".yml"]}
                          multiple={false}
                          boxContent={(props: any) => (
                            <FileUploader {...props} />
                          )}
                          onFileChange={(files: File[] | undefined) => {
                            if (files && files.length > 0) {
                              setUploadedFile(files[0].name);
                            } else {
                              setUploadedFile(undefined);
                            }
                          }}
                        />
                        <Field name="apiStandardVersion">
                          {({ input }) => (
                            <FormControl style={{ minWidth: 280, marginTop: "16px" }}>
                              <InputLabel id="api-standard-version-label">API Standard Version</InputLabel>
                              <Select
                                {...input}
                                labelId="api-standard-version-label"
                                value={input.value || apiStandardVersion}
                                onChange={(event) => {
                                  input.onChange(event.target.value);
                                  setApiStandardVersion(event.target.value as string);
                                }}
                              >
                                {apiStandardVersions.map((item) => (
                                  <MenuItem key={item.id} value={item.version}>
                                    {item.version}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        </Field>
                      </Box>
                    </form>
                  );
                }}
              />
            </Box>
          </Box>
        )}
        {uploadFile && (
          <Box>
            <Box marginBottom={"10px"}>
            {state.error && <Alert severity="error">{resolveAPIErrorMessage(state.errorValue)}</Alert>}
            </Box>
            <SpecViewer specName={uploadFile}></SpecViewer>
          </Box>
        )}
      </DialogContent>
      <DialogActions
        style={{ marginTop: "20px", marginBottom: "5px", marginRight: "20px" }}
      >
        <Button
          onClick={() => {
            onClose();
            setTimeout(() => {
            clear();
          }, 100);
          }}
          color="secondary"
          variant="outlined"
        >
          <FormattedMessage defaultMessage="Cancel" />
        </Button>
        <Button
          onClick={() => {
            const formState = formRef.current?.getState();
            if (!formState?.values?.apiStandardVersion) {
              setApiStandardVersionError("Unable to upload specification because API standard versions are unavailable. Please try again later.");
              return;
            }
            if (formState?.values?.specFile) {
              submitData = new FormData(); // Reset FormDatasa
              submitData.append("file", formState.values.specFile[0]);
              submitData.append("apiStandardVersion", formState.values.apiStandardVersion);
              subscribe();
            }
          }}
          color="primary"
          variant="contained"
          disabled={uploadFile === undefined || !!apiStandardVersionError}
        >
          <FormattedMessage defaultMessage="Upload" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
